/**
 * 🎨🐦‍🔥 HeroPortraitCard — the selfie → Phoenix Hero avatar UI.
 *
 * Flow:
 *   1. User taps "Take a selfie" → the file input opens (rear/front camera on mobile)
 *   2. Preview shows the selfie + optional "extra guidance" text
 *   3. Tap "Generate" → 8-15 s of Nano Banana call → new portrait appears
 *   4. Portrait auto-set as active avatar, appears everywhere via <HeroAvatar/>
 *   5. History drawer lets user re-activate an older portrait (no regen cost)
 *
 * Accessibility & control:
 *   - Every action tells the user what it costs BEFORE it costs anything
 *   - Free quota badge always visible (X of 3 this week)
 *   - "Reset to default silhouette" button lets user OPT OUT any time
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { invalidateAvatarCache } from "@/components/HeroAvatar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HeroPortraitCard() {
  const [portrait,    setPortrait]  = useState(null);
  const [quota,       setQuota]     = useState(null);
  const [resync,      setResync]    = useState(null);
  const [file,        setFile]      = useState(null);
  const [preview,     setPreview]   = useState(null);
  const [note,        setNote]      = useState("");
  const [busy,        setBusy]      = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history,     setHistory]   = useState([]);
  const fileRef = useRef(null);

  const refresh = async () => {
    try {
      const [{ data: d1 }, { data: d2 }] = await Promise.all([
        axios.get(`${API}/hero/portrait`),
        axios.get(`${API}/hero/portrait/history`),
      ]);
      setPortrait(d1?.portrait || null);
      setQuota(d1?.quota || null);
      setResync(d1?.resync || null);
      setHistory(d2?.history || []);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { refresh(); }, []);

  const pickFile = () => fileRef.current?.click();

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 12_000_000) {
      toast.error("Photo is over 12 MB — try a smaller one.");
      return;
    }
    setFile(f);
    const url = URL.createObjectURL(f);
    setPreview(url);
  };

  const generate = async () => {
    if (!file) { toast.error("Pick a selfie first."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("selfie", file);
      if (note) fd.append("custom_note", note);
      const { data } = await axios.post(`${API}/hero/portrait/generate`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90_000,
      });
      setPortrait(data.portrait);
      setQuota(data.quota);
      setFile(null); setPreview(null); setNote("");
      invalidateAvatarCache();
      await refresh();
      toast.success(
        data.used_free
          ? `Vitality Avatar ready — ${data.quota.free_remaining} free left this week.`
          : `Vitality Avatar ready — ${data.od_spent} OD spent.`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed. Try again.");
    } finally { setBusy(false); }
  };

  const activate = async (portrait_id) => {
    try {
      const { data } = await axios.post(`${API}/hero/portrait/pick-history`, { portrait_id });
      setPortrait(data.portrait);
      invalidateAvatarCache();
      setHistoryOpen(false);
      toast.success("Portrait activated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't activate that portrait.");
    }
  };

  const removePortrait = async () => {
    if (!confirm("Reset to the default silhouette? Your history is kept.")) return;
    try {
      await axios.delete(`${API}/hero/portrait`);
      setPortrait(null);
      invalidateAvatarCache();
      toast.success("Reverted to default avatar.");
    } catch (e) {
      toast.error("Couldn't reset.");
    }
  };

  const freeLeft = quota?.free_remaining ?? 0;
  const costLabel = freeLeft > 0
    ? `${freeLeft} of ${quota?.free_per_week ?? 3} free left this week`
    : `${quota?.od_cost_per_regen ?? 25} OD next regen (balance: ${quota?.od_balance ?? 0})`;

  return (
    <div className="rounded-2xl border border-[#6D28D9]/40 bg-gradient-to-br from-[#0f0518] via-[#080310] to-[#1b0a24] p-4 mb-6 shadow-[0_0_20px_rgba(109,40,217,0.15)]"
         data-testid="hero-portrait-card">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className="text-2xl">🪞</div>
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#A78BFA] font-bold">
              Vitality Avatar
            </div>
            <div className="text-[10px] text-neutral-500">
              Photorealistic body-twin · tracks your weight, BMI, body-fat live
            </div>
          </div>
        </div>
        <div className="text-[9px] uppercase tracking-widest text-neutral-400 text-right"
             data-testid="hero-portrait-quota">
          {costLabel}
        </div>
      </div>

      {/* 🔔 Resync nudge — body has shifted since last avatar */}
      {resync?.needed && (
        <div className="mb-3 rounded-xl border border-emerald-500/50 bg-emerald-500/10 px-3 py-2 flex items-center justify-between gap-2"
             data-testid="hero-portrait-resync-nudge">
          <div className="text-[11px] text-emerald-200 leading-tight">
            💪 {resync.reason}
          </div>
          <button
            onClick={pickFile}
            className="text-[10px] px-2 py-1 rounded-lg bg-emerald-500/25 border border-emerald-400/60 text-emerald-100 font-bold hover:bg-emerald-500/40"
            data-testid="hero-portrait-resync-btn"
          >
            Update
          </button>
        </div>
      )}

      <div className="grid grid-cols-[112px_1fr] gap-3 items-center">
        {/* Current portrait */}
        <div className="relative">
          {portrait?.data_url ? (
            <img
              src={portrait.data_url}
              alt="Your Vitality Avatar"
              className="w-28 h-28 rounded-full border-2 border-[#6D28D9] object-cover shadow-[0_0_16px_rgba(109,40,217,0.4)]"
              data-testid="hero-portrait-current"
            />
          ) : (
            <div className="w-28 h-28 rounded-full border-2 border-[#6D28D9]/50 bg-gradient-to-br from-[#4C1D95] to-[#1E1B4B] flex items-center justify-center text-4xl text-white/70"
                 data-testid="hero-portrait-none">
              🪞
            </div>
          )}
          {portrait?.bio_at_gen && Object.keys(portrait.bio_at_gen || {}).length > 0 && (
            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[8px] uppercase tracking-widest text-emerald-300 bg-black/70 px-1.5 py-0.5 rounded-full whitespace-nowrap"
                 data-testid="hero-portrait-bio-badge">
              live • from Vitality
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="flex flex-col gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={onFile}
            className="hidden"
            data-testid="hero-portrait-file-input"
          />
          <button
            onClick={pickFile}
            disabled={busy}
            className="w-full py-2 rounded-xl bg-gradient-to-br from-[#6D28D9] to-[#7C3AED] text-white font-bold text-sm hover:brightness-110 disabled:opacity-60"
            data-testid="hero-portrait-pick"
          >
            {portrait ? "📸 Update from new photo" : "📸 Snap a full-body photo"}
          </button>
          <div className="flex gap-2">
            <button
              onClick={() => { setHistoryOpen(true); }}
              disabled={history.length === 0}
              className="flex-1 py-1.5 rounded-lg bg-black/40 border border-[#6D28D9]/40 text-[11px] text-[#C4B5FD] hover:bg-black/60 disabled:opacity-40"
              data-testid="hero-portrait-history"
            >
              History ({history.length})
            </button>
            {portrait && (
              <button
                onClick={removePortrait}
                className="px-2 py-1.5 rounded-lg bg-neutral-900 border border-neutral-800 text-[11px] text-neutral-500 hover:text-neutral-300"
                data-testid="hero-portrait-reset"
              >
                Reset
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 📊 Biometrics baked into the active avatar */}
      {portrait?.bio_at_gen && Object.keys(portrait.bio_at_gen || {}).length > 0 && (
        <div className="mt-3 rounded-lg bg-black/40 border border-[#6D28D9]/30 px-3 py-2"
             data-testid="hero-portrait-bio-footer">
          <div className="text-[9px] uppercase tracking-widest text-neutral-500 mb-1">
            Baked into this avatar
          </div>
          <div className="flex flex-wrap gap-2 text-[11px]">
            {portrait.bio_at_gen.weight_kg && (
              <span className="text-neutral-300">
                <span className="text-neutral-500">Weight</span>{" "}
                <b className="text-white">{Math.round(portrait.bio_at_gen.weight_kg)} kg</b>
              </span>
            )}
            {portrait.bio_at_gen.body_fat_pct != null && (
              <span className="text-neutral-300">
                <span className="text-neutral-500">Body-fat</span>{" "}
                <b className="text-white">{portrait.bio_at_gen.body_fat_pct}%</b>
              </span>
            )}
            {portrait.bio_at_gen.waist_cm && (
              <span className="text-neutral-300">
                <span className="text-neutral-500">Waist</span>{" "}
                <b className="text-white">{Math.round(portrait.bio_at_gen.waist_cm)} cm</b>
              </span>
            )}
            {portrait.generated_at && (
              <span className="text-neutral-500 ml-auto">
                {new Date(portrait.generated_at).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Preview + generate */}
      {preview && (
        <div className="mt-3 rounded-xl bg-black/50 border border-[#6D28D9]/40 p-3"
             data-testid="hero-portrait-preview">
          <div className="flex gap-3 items-center mb-2">
            <img src={preview} alt="Selfie preview"
                 className="w-16 h-16 rounded-lg object-cover border border-neutral-800" />
            <div className="flex-1 text-[11px] text-neutral-400 leading-tight">
              <span className="text-[#A78BFA] font-bold">Photorealistic body-twin.</span><br />
              Face + body preserved · silhouette matches your latest logged weight, BMI, body-fat &amp; waist.
            </div>
          </div>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional guidance — e.g. 'keep my glasses', 'add a red cape'"
            maxLength={280}
            className="w-full h-14 mb-2 px-2 py-1.5 rounded-lg bg-black/40 border border-neutral-800 text-xs text-neutral-100 focus:border-[#6D28D9]/60 focus:outline-none"
            data-testid="hero-portrait-note"
          />
          <div className="flex gap-2">
            <button
              onClick={() => { setFile(null); setPreview(null); setNote(""); }}
              disabled={busy}
              className="flex-1 py-2 rounded-lg bg-neutral-900 border border-neutral-800 text-sm text-neutral-300"
              data-testid="hero-portrait-cancel"
            >
              Cancel
            </button>
            <button
              onClick={generate}
              disabled={busy}
              className="flex-[2] py-2 rounded-lg bg-gradient-to-br from-[#F97316] to-[#DC2626] text-white font-black text-sm hover:brightness-110 disabled:opacity-60"
              data-testid="hero-portrait-generate"
            >
              {busy ? "🪞 Rendering your body-twin…" : "🪞 Generate Vitality Avatar"}
            </button>
          </div>
        </div>
      )}

      {/* History drawer */}
      {historyOpen && (
        <div className="fixed inset-0 z-[110] bg-black/70 flex items-center justify-center p-4"
             onClick={() => setHistoryOpen(false)}
             data-testid="hero-portrait-history-modal">
          <div className="rounded-2xl bg-neutral-950 border border-[#6D28D9]/50 max-w-md w-full p-5 max-h-[80vh] overflow-auto"
               onClick={(e) => e.stopPropagation()}>
            <div className="text-sm font-bold text-[#A78BFA] mb-3">
              Portrait History <span className="text-neutral-500 text-xs font-normal">(last 8)</span>
            </div>
            {history.length === 0 && (
              <div className="text-[11px] text-neutral-500 italic">No older portraits yet.</div>
            )}
            <div className="grid grid-cols-3 gap-2">
              {history.map((h) => (
                <button
                  key={h.portrait_id}
                  onClick={() => activate(h.portrait_id)}
                  className={`rounded-lg border p-1 hover:bg-black/60 ${
                    portrait?.portrait_id === h.portrait_id
                      ? "border-[#F97316] ring-2 ring-[#F97316]/40"
                      : "border-neutral-800"}`}
                  data-testid={`hero-portrait-history-item-${h.portrait_id}`}
                >
                  <img src={h.data_url} alt="past portrait"
                       className="w-full aspect-square rounded object-cover" />
                  <div className="text-[9px] text-neutral-500 mt-1 truncate">
                    {new Date(h.generated_at).toLocaleDateString()}
                  </div>
                </button>
              ))}
            </div>
            <button
              onClick={() => setHistoryOpen(false)}
              className="mt-4 w-full py-2 rounded-lg bg-neutral-900 border border-neutral-800 text-sm text-neutral-300"
              data-testid="hero-portrait-history-close"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
